import pythonApiClient from "./pythonBaseApi";

const asignacionCamasTrabajadoresApi = {
  /**
   * Validar archivo Excel de asignaciones de camas
   * @param {File} file - Archivo Excel con columna DNI
   * @param {number} proyectoId - ID del proyecto
   * @returns {Promise} Respuesta de validación
   */
  validateFile: (file, proyectoId) => {
    const formData = new FormData();
    formData.append("file", file);
    
    return pythonApiClient.post(
      "/procesos/hoteleria/reserva-automatica-camas/validate",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Proyecto-ID": proyectoId,
        },
      }
    );
  },

  /**
   * Procesar asignaciones de camas desde archivo Excel (reserva automática)
   * @param {File} file - Archivo Excel con columna DNI
   * @param {number} proyectoId - ID del proyecto
   * @returns {Promise} Resultado del procesamiento
   */
  procesarAsignaciones: (file, proyectoId) => {
    const formData = new FormData();
    formData.append("file", file);
    
    return pythonApiClient.post(
      "/procesos/hoteleria/reserva-automatica-camas",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Proyecto-ID": proyectoId,
        },
      }
    );
  },
  
  /**
   * Realizar Check-in individual de un trabajador
   * @param {string} dni - DNI del trabajador
   * @param {number} proyectoId - ID del proyecto
   * @returns {Promise} Resultado del check-in
   */
  checkin: (dni, proyectoId, estacionCodigo) => {
    return pythonApiClient.post(
      "/procesos/hoteleria/check-in/",
      { dni, estacion_codigo: estacionCodigo },
      {
        headers: {
          "X-Proyecto-ID": proyectoId,
        },
      }
    );
  },

  /**
   * Realizar Re-impresión de ticket
   * @param {string} dni - DNI del trabajador
   * @param {number} proyectoId - ID del proyecto
   * @returns {Promise} Resultado de re-impresión
   */
  reprint: (dni, proyectoId, estacionCodigo) => {
    return pythonApiClient.post(
      "/procesos/hoteleria/re-impresion/",
      { dni, estacion_codigo: estacionCodigo },
      {
        headers: {
          "X-Proyecto-ID": proyectoId,
        },
      }
    );
  },

  /**
   * Realizar Check-out individual de un trabajador
   * @param {string} dni - DNI del trabajador
   * @param {number} proyectoId - ID del proyecto
   * @returns {Promise} Resultado del check-out
   */
  buscarCheckoutIndividual: (dni, proyectoId, autoPadDni8 = false) => {
    return pythonApiClient.get("/procesos/hoteleria/check-out/buscar", {
      params: {
        dni: dni.trim(),
        auto_pad_dni_8: Boolean(autoPadDni8),
      },
      headers: {
        "X-Proyecto-ID": proyectoId,
      },
    });
  },

  checkout: (dni, proyectoId, autoPadDni8 = false) => {
    return pythonApiClient.post(
      "/procesos/hoteleria/check-out/",
      { dni, auto_pad_dni_8: Boolean(autoPadDni8) },
      {
        headers: {
          "X-Proyecto-ID": proyectoId,
        },
      }
    );
  },

  validarCheckoutMasivo: (file, proyectoId, autoPadDni8) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("auto_pad_dni_8", autoPadDni8 ? "true" : "false");
    return pythonApiClient.post(
      "/procesos/hoteleria/check-out/validar-masivo",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Proyecto-ID": proyectoId,
        },
      }
    );
  },

  checkoutMasivo: (file, proyectoId, autoPadDni8) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("auto_pad_dni_8", autoPadDni8 ? "true" : "false");
    return pythonApiClient.post(
      "/procesos/hoteleria/check-out/masivo",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Proyecto-ID": proyectoId,
        },
      }
    );
  },

  /**
   * Obtener estado de ocupabilidad del campamento por módulo
   * Usa el proyecto del header X-Proyecto-ID configurado en pythonBaseApi
   */
  getEstadoCampamento: () => {
    return pythonApiClient.get(
      "/procesos/hoteleria/reserva-automatica-camas/estado-campamento"
    );
  },

  /**
   * Buscar trabajadores por documento o nombre/apellidos
   * @param {string} busqueda - Texto a buscar
   * @param {number} proyectoId - ID del proyecto
   * @returns {Promise} Lista de trabajadores encontrados
   */
  buscarTrabajadores: (busqueda, proyectoId) => {
    return pythonApiClient.get(
      "/procesos/hoteleria/reserva-automatica-camas/buscar-trabajadores",
      {
        params: { busqueda },
        headers: {
          "X-Proyecto-ID": proyectoId,
        },
      }
    );
  },

  /**
   * Crear reserva individual de cama para un trabajador (reserva automática)
   * @param {number} trabajadorId - ID del trabajador
   * @param {number} proyectoId - ID del proyecto
   * @param {{ fechaInicioEstadia: string, fechaFinEstadia: string }} fechas - Fechas obligatorias YYYY-MM-DD
   * @returns {Promise} Resultado de la reserva
   */
  crearReservaIndividual: (trabajadorId, proyectoId, fechas = {}) => {
    const formData = new FormData();
    formData.append("trabajador_id", trabajadorId);
    if (fechas.fechaInicioEstadia) {
      formData.append("fecha_inicio_estadia", fechas.fechaInicioEstadia);
    }
    if (fechas.fechaFinEstadia) {
      formData.append("fecha_fin_estadia", fechas.fechaFinEstadia);
    }
    
    return pythonApiClient.post(
      "/procesos/hoteleria/reserva-automatica-camas/reserva-individual",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Proyecto-ID": proyectoId,
        },
      }
    );
  },

  /**
   * Obtener detalle de camas ocupadas (con check-in)
   * @returns {Promise} Lista de camas ocupadas con detalle de trabajador
   */
  getDetalleOcupadas: () => {
    return pythonApiClient.get(
      "/procesos/hoteleria/reserva-automatica-camas/detalle-ocupadas"
    );
  },

  /**
   * Obtener detalle de camas reservadas (sin check-in)
   * @returns {Promise} Lista de camas reservadas con detalle de trabajador
   */
  getDetalleReservadas: () => {
    return pythonApiClient.get(
      "/procesos/hoteleria/reserva-automatica-camas/detalle-reservadas"
    );
  },

  /**
   * Obtener detalle de camas libres
   * @returns {Promise} Lista de camas libres con ubicación
   */
  getDetalleLibres: () => {
    return pythonApiClient.get(
      "/procesos/hoteleria/reserva-automatica-camas/detalle-libres"
    );
  },
};

export default asignacionCamasTrabajadoresApi;
